import React, { useEffect, useMemo, useState } from 'react';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { isCompanyAdminUser } from "@shared/utils/AccessControl";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { SpaceBetweenRow } from '@features/settings/pages/StyleComponents';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular, BodyLarge } from '@shared/ui/atoms/fonts/Body';
import {
  AutomaticCheckinLog,
  getAutomaticCheckins,
  getIgnoredAutomaticCheckins,
  IgnoredAutomaticCheckin,
} from '@features/reporting/services/ReportingService';
import { failureNotification } from "@shared/store/notification.duck";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import Colours from '@shared/ui/atoms/Colours';
import { PRETTY_DATE_FORMAT } from '@shared/utils/DateUtils';

export function AutomaticOfficeCheckInReportingPage() {
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [checkins, setCheckins] = useState<AutomaticCheckinLog[]>([]);
  const [ignored, setIgnored] = useState<IgnoredAutomaticCheckin[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasEverHadCheckins, setHasEverHadCheckins] = useState<boolean | null>(null);
  const currentUser = useSelector(selectCurrentUser);
  const canViewReport = useMemo(() => isCompanyAdminUser(currentUser), [currentUser]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (canViewReport) {
      setLoading(true);
      getAutomaticCheckins(selectedDate)
        .then(data => {
          setCheckins(data);
          if (data.length > 0) {
            setHasEverHadCheckins(true);
          } else if (hasEverHadCheckins === null) {
            setHasEverHadCheckins(false);
          }
        })
        .catch(() => dispatch(failureNotification("Failed to get automatic check-in data")))
        .finally(() => setLoading(false));
      getIgnoredAutomaticCheckins(selectedDate)
        .then(setIgnored)
        .catch(() => setIgnored([]));
    }
  }, [canViewReport, selectedDate, dispatch]);

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      {loading && <LoadingSpinner fullScreen={true} />}
      <div>
        {canViewReport ? <>
          <HeadlineLarge style={{marginBottom: 24}}>Automatic Office Check-ins</HeadlineLarge>

          {hasEverHadCheckins === false && !loading && (
            <SetupBanner>
              <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 8}}>No check-ins have been recorded yet</BodyLarge>
              <BodyRegular>It looks like automatic office check-in hasn't sent any data yet. Please ensure your network detection is configured and sending requests. If you need help, contact <a href="mailto:support@team-today.com">support@team-today.com</a>.</BodyRegular>
            </SetupBanner>
          )}

          <SpaceBetweenRow>
            <DateFlicker dateScale={DateScale.Day} updateGlobalDate={false} onChange={date => setSelectedDate(date)} date={selectedDate} disabled={loading} dateFormat={PRETTY_DATE_FORMAT} />
          </SpaceBetweenRow>

          {checkins.length === 0 && !loading && (
            <BodyRegular colour={Colours.darkGrey} style={{marginTop: 24}}>No check-ins found for this date.</BodyRegular>
          )}

          {checkins.length > 0 && (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Office</Th>
                    <Th>Network</Th>
                    <Th>MAC Address</Th>
                    <Th>Time</Th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map(checkin => (
                    <tr key={checkin.id}>
                      <Td>{[checkin.firstName, checkin.lastName].filter(Boolean).join(' ') || '-'}</Td>
                      <Td>{checkin.email || 'Deleted user'}</Td>
                      <Td>{checkin.officeName || '-'}</Td>
                      <Td>{checkin.networkName || '-'}</Td>
                      <Td>{checkin.macAddress || '-'}</Td>
                      <Td title={checkin.timeZone}>
                        {moment(checkin.dateTime).format('HH:mm:ss')}{checkin.timeZoneName ? ` ${checkin.timeZoneName}` : ''}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}

          <BodyLarge weight={600} style={{marginTop: 40, marginBottom: 4}}>Ignored check-ins</BodyLarge>
          <BodyRegular colour={Colours.darkGrey}>
            Requests from your approved office IP addresses on this day that could not be recorded, and how to fix them.
          </BodyRegular>

          {ignored.length === 0 && !loading && (
            <BodyRegular colour={Colours.darkGrey} style={{marginTop: 16}}>None on this day.</BodyRegular>
          )}

          {ignored.length > 0 && (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Reason</Th>
                    <Th>Detail</Th>
                    <Th>Office</Th>
                    <Th>Requests</Th>
                    <Th>Last received</Th>
                    <Th>What to do</Th>
                  </tr>
                </thead>
                <tbody>
                  {ignored.map(row => {
                    const explanation = explainIgnoredCheckin(row);
                    return (
                      <tr key={row.id}>
                        <Td>{explanation.reason}</Td>
                        <Td><DetailValue>{row.detail || '-'}</DetailValue></Td>
                        <Td>{row.officeName || '-'}</Td>
                        <Td>{row.requestCount}</Td>
                        <Td>{moment.utc(row.lastSeenAt).local().format('HH:mm')}</Td>
                        <Td>{explanation.fix}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </> : <>
          <AccessRequired visible={!canViewReport} companyAdminsRequired={true} />
        </>}
      </div>
    </ReportingContainer>
  );
}

function explainIgnoredCheckin(row: IgnoredAutomaticCheckin): { reason: string, fix: string } {
  switch (row.reason) {
    case 'MISSING_FIELDS':
      return { reason: 'Missing details', fix: 'Each request needs a dateTime and an email address, MAC address or Entra ID.' };
    case 'UNKNOWN_PERSON':
      if (row.detailType === 'MAC_ADDRESS') {
        return { reason: 'Device not linked to anyone', fix: 'Add this MAC address to the person under Settings > Manage current users.' };
      }
      if (row.detailType === 'EMAIL') {
        return { reason: 'Email address not found', fix: 'Check this is the email address the person uses in Team Today.' };
      }
      return { reason: 'Microsoft account not linked', fix: "Send the person's email address with each request as well." };
    case 'MAC_IN_TWO_COMPANIES':
      return { reason: 'Device registered with another company', fix: 'Contact support@team-today.com.' };
    case 'NETWORK_NOT_MAPPED':
      return { reason: 'Network not mapped to an office', fix: 'If this is an office network, add it to the office under Settings > Offices and meeting rooms > Automatic Check-in.' };
    default:
      return { reason: row.reason, fix: '' };
  }
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const SetupBanner = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  a {
    color: ${Colours.blue};
    font-weight: 600;
  }
`

const TableWrapper = styled.div`
  margin-top: 24px;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  white-space: nowrap;
`

const DetailValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  word-break: break-all;
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
`
